import { executeCommand } from '../Utils/SendCommandToACC';
import { isMockMode } from '../Knowledge/mockMode';
import { runWith } from '../Utils/accClient';
import { CODE_ID_RE, CODE_TOKEN_RE } from './codeToken';

/**
 * Protected codes API client: the lockbox code, per-trainer alarm codes and
 * anything else an SOP must not print. Authors write `{{code:<codeId>}}` in
 * markdown; `MarkdownWithCodes` swaps each token for a `ProtectedCode` chip,
 * and every reveal is logged server-side (`list_protected_code_reveals`).
 *
 * `run` is the Utils/accClient contract: every thrown error carries `code`
 * (server errorCode or null), `details` and `isNetwork`. The mock store is
 * imported on demand because the chip renders inside the main chunk (task
 * descriptions).
 */

const dispatch = (command, args = []) =>
    (isMockMode()
        ? import('./mockCodesData').then((m) => m.mockCodesExecuteCommand(command, args))
        : executeCommand(command, args));

export const run = runWith(dispatch);

// --- Commands ---

/** All roles. `context` says where the chip was (`sop:<articleId>:<stepId>`); it lands in the reveal log. */
export const revealProtectedCode = (codeId, context = '') => run('reveal_protected_code', [codeId, context]);
export const listProtectedCodes = () => run('list_protected_codes', []);
/** Admin only. `payload` is `{label?, value?, perUser?: {name: value|null}, clear?}`. */
export const setProtectedCode = (codeId, payload) => run('set_protected_code', [codeId, payload]);
/** Manager and Admin. Newest first. */
export const listProtectedCodeReveals = (codeId = null, days = 30) => run('list_protected_code_reveals', [codeId, days]);

// --- Label cache ---

let labelsPromise = null;

/**
 * `{ codeId: label }` from one `list_protected_codes` read shared by every chip
 * and picker on the page, or `null` when the read was refused (a Lambda
 * without the command). Like `useTrainingEnabled`, a refusal is kept for the
 * session so every chip mount is not another 403; a network error is
 * forgotten so the next mount retries.
 */
export const loadCodeLabels = () => {
    if (!labelsPromise) {
        labelsPromise = listProtectedCodes()
            .then((data) => Object.fromEntries((data?.codes || []).map((c) => [c.codeId, c.label || ''])))
            .catch((e) => {
                if (e?.isNetwork) labelsPromise = null;
                return null;
            });
    }
    return labelsPromise;
};

/** After a save or in tests: the next chip reads the list again. */
export const resetCodeLabels = () => {
    labelsPromise = null;
};

// --- Pure helpers ---

export { CODE_TOKEN_RE, CODE_ID_RE };

export const isValidCodeId = (codeId) => CODE_ID_RE.test(String(codeId || ''));

/**
 * Markdown source as `[{type:'text', text} | {type:'code', codeId}]`, in
 * order, with empty text runs dropped. `split` on the capturing group is
 * stateless, unlike `exec` on the shared global regex.
 */
export const splitCodeTokens = (text) => {
    const parts = String(text ?? '').split(CODE_TOKEN_RE);
    const out = [];
    parts.forEach((part, i) => {
        if (i % 2 === 1) out.push({ type: 'code', codeId: part });
        else if (part) out.push({ type: 'text', text: part });
    });
    return out;
};

export const hasCodeTokens = (text) => splitCodeTokens(text).some((p) => p.type === 'code');

export const codeToken = (codeId) => `{{code:${codeId}}}`;

/** `text` with `token` on the end, one space apart unless the text is empty or already ends in whitespace. */
export const appendCodeToken = (text, token) => {
    const base = String(text ?? '');
    return `${base}${base && !/\s$/.test(base) ? ' ' : ''}${token}`;
};

/** `sop:<articleId>:<stepId>`; empty ids are dropped so a missing step id never leaves a trailing colon. */
export const codeContextFor = (kind, ...ids) =>
    [kind, ...ids].map((v) => String(v ?? '').trim()).filter(Boolean).join(':');

/** `alarm-disarm` reads `Alarm disarm` while the label list loads. */
export const humanizeCodeId = (codeId) => {
    const words = String(codeId || '').replace(/-+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/** A codeId from free text: lower-case, dashes, at most 40 characters. */
export const slugifyCodeId = (text) =>
    String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/g, '');

const OUTCOME_LABELS = { shown: 'Shown', no_value: 'No code set', not_found: 'Not found', refused: 'Refused' };

export const revealOutcomeLabel = (outcome) => OUTCOME_LABELS[outcome] || String(outcome || '');

/** `Hides in 12s`. */
export const hidesInLabel = (seconds) => `Hides in ${Math.max(0, Math.ceil(Number(seconds) || 0))}s`;

/** `Sep 14, 3:05 PM`, or '' when unparseable. */
export const revealTime = (iso) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
